import type { MatchState, PlayerState } from './state';

// Explicit field-by-field deep clone (§4.3). `step` runs on a clone so it never mutates its
// arguments (T-ARCH-003). Kept explicit (not structuredClone) for speed and to force a compile error
// here whenever a new state field is added but not cloned.

export function clonePlayer(p: PlayerState): PlayerState {
  return {
    id: p.id,
    team: p.team,
    pos: { x: p.pos.x, y: p.pos.y },
    vel: { x: p.vel.x, y: p.vel.y },
    facing: p.facing,
    z: p.z,
    vz: p.vz,
    stamina: p.stamina,
    action: p.action,
    actionTicks: p.actionTicks,
    charge: p.charge ? { button: p.charge.button, ticks: p.charge.ticks } : null,
    sprinting: p.sprinting,
    prevButtons: p.prevButtons,
    shouting: p.shouting,
    calling: p.calling,
    offsideLiable: p.offsideLiable,
    intentBits: p.intentBits,
    intentTick: p.intentTick,
    card: p.card,
    sentOff: p.sentOff,
    suspendedUntilTick: p.suspendedUntilTick,
    isGk: p.isGk,
    punchHit: p.punchHit,
    lastContact: p.lastContact
      ? { by: p.lastContact.by, tick: p.lastContact.tick, impulse: { x: p.lastContact.impulse.x, y: p.lastContact.impulse.y } }
      : null,
  };
}

export function cloneState(s: MatchState): MatchState {
  return {
    tick: s.tick,
    rng: s.rng,
    phase: s.phase,
    celebration: s.celebration
      ? {
          scorer: s.celebration.scorer,
          team: s.celebration.team,
          celebrants: [...s.celebration.celebrants],
        }
      : null,
    phaseTicksLeft: s.phaseTicksLeft,
    restart: s.restart ? { kind: s.restart.kind, spot: { x: s.restart.spot.x, y: s.restart.spot.y }, team: s.restart.team, taker: s.restart.taker } : null,
    half: s.half,
    clockTicks: s.clockTicks,
    attackDir: [s.attackDir[0], s.attackDir[1]],
    score: [s.score[0], s.score[1]],
    players: s.players.map(clonePlayer),
    ball: {
      pos: { x: s.ball.pos.x, y: s.ball.pos.y, z: s.ball.pos.z },
      vel: { x: s.ball.vel.x, y: s.ball.vel.y, z: s.ball.vel.z },
      spin: { x: s.ball.spin.x, y: s.ball.spin.y, z: s.ball.spin.z },
      possessor: s.ball.possessor,
      gkLock: s.ball.gkLock,
      claimCooldownTicks: s.ball.claimCooldownTicks,
    claimCooldownOnly: s.ball.claimCooldownOnly,
    kickedBy: s.ball.kickedBy,
      trappedTicks: s.ball.trappedTicks,
      shotPending: s.ball.shotPending,
      lastTouch: s.ball.lastTouch
        ? { entity: s.ball.lastTouch.entity, team: s.ball.lastTouch.team, tick: s.ball.lastTouch.tick }
        : null,
    },
    referee: {
      pos: { x: s.referee.pos.x, y: s.referee.pos.y },
      vel: { x: s.referee.vel.x, y: s.referee.vel.y },
      viewDir: s.referee.viewDir,
      aiTargetPos: { x: s.referee.aiTargetPos.x, y: s.referee.aiTargetPos.y },
      scanTicksLeft: s.referee.scanTicksLeft,
    },
    discipline: { perPlayer: s.discipline.perPlayer.map((d) => ({ ...d })) },
    advantage: s.advantage
      ? {
          offender: s.advantage.offender,
          victim: s.advantage.victim,
          victimTeam: s.advantage.victimTeam,
          severity: s.advantage.severity,
          ticksLeft: s.advantage.ticksLeft,
          restart: {
            kind: s.advantage.restart.kind,
            spot: { x: s.advantage.restart.spot.x, y: s.advantage.restart.spot.y },
            team: s.advantage.restart.team,
            taker: s.advantage.restart.taker,
          },
        }
      : null,
    cardQueue: s.cardQueue.map((c) => ({ ...c })),
    conditions: s.conditions,
    mode: { ...s.mode },
  };
}
